/**
 * "Under Grid" — original platformer level #2 (difficulty 3/10).
 * Two key-and-lock doors, a one-way climb, and a jetpack duct. Rightward-only
 * completable (solver requirement), built to reward exploration pacing.
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function push(o: Omit<LevelObject, 'id'>): void {
  objects.push({ id: nextId++, ...o });
}

const spike = (bx: number, by = 0): void => push({ type: 'spike', ...cell(bx, by) });
const block = (bx: number, by = 0): void => push({ type: 'block', ...cell(bx, by) });
const slope = (bx: number, by: number, dir: 'up' | 'down'): void =>
  push({ type: 'slope', ...cell(bx, by), props: { dir, grade: 1 } });
const oneway = (bx: number, by: number): void => push({ type: 'oneway', ...cell(bx, by) });
const portal = (bx: number, kind: string, by = 1): void =>
  push({ type: 'portal', ...cell(bx, by), props: { kind } });
const checkpoint = (bx: number): void => push({ type: 'checkpoint', ...cell(bx, 1) });
const key = (bx: number, by: number, itemId: number): void =>
  push({ type: 'collectible', ...cell(bx, by), props: { kind: 'key', itemId } });
const lockDoor = (bx: number, itemId: number): void => {
  for (let by = 0; by <= 2; by++)
    push({ type: 'lock', ...cell(bx, by), props: { itemId, needed: 1 } });
};
const ceiling = (bx0: number, bx1: number, by: number): void => {
  for (let bx = bx0; bx <= bx1; bx++) block(bx, by);
};

// --- Entry hall: pits and the first key (0–44) ---------------------------------
spike(10);
spike(11);
spike(20);
spike(21);
checkpoint(26);
key(34, 1, 1);
lockDoor(40, 1);
push({ type: 'collectible', ...cell(37, 2), props: { kind: 'shard' } });

// --- Ramp and one-way climb (44–66) ----------------------------------------------
slope(46, 0, 'up');
block(47, 0);
block(48, 0);
slope(49, 0, 'down');
oneway(54, 1);
oneway(58, 2);
ceiling(60, 64, 2); // walkway at the top of the climb
checkpoint(68);

// --- Jetpack duct (70–102) --------------------------------------------------------
portal(70, 'form:glider');
ceiling(72, 100, 6);
for (let bx = 76; bx <= 96; bx += 2) spike(bx);
portal(102, 'form:runner', 1);
portal(102, 'form:runner', 4);
push({ type: 'collectible', ...cell(88, 4), props: { kind: 'shard' } });

// --- Second key, second door, finale (104–130) --------------------------------------
key(108, 1, 2);
checkpoint(110);
spike(113);
spike(114);
lockDoor(118, 2);
spike(123);
spike(124);
push({ type: 'collectible', ...cell(121, 2), props: { kind: 'shard' } });

push({ type: 'endgate', ...cell(130, 2) });

export const UNDER_GRID: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Under Grid',
    author: 'Shape Shifters Team',
    description: 'Two keys. Two doors. One way down.',
    revision: 1,
    createdAt: '2026-07-24T00:00:00.000Z',
    updatedAt: '2026-07-24T00:00:00.000Z',
    difficulty: 3,
    tags: ['platformer', 'official'],
  },
  song: { id: 'undercurrent', bpm: 112, offsetMs: 0 },
  settings: {
    mode: 'platformer',
    speed: 'one',
    startForm: 'runner',
    accentColor: '#38bdf8',
    hazardColor: '#fb7185',
    backgroundColor: '#020d17',
  },
  objects,
};
